import { motion } from 'framer-motion';
import { useContent } from '@/context/ContentContext';
import { Check, ArrowUpRight } from 'lucide-react';

export default function Pricing() {
  const { t } = useContent();
  if (!t) return null;
  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-20 sm:py-24 md:py-40 bg-paper">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start md:items-end mb-12 sm:mb-16 md:mb-24">
          <div className="md:col-span-3 font-mono text-[10px] uppercase tracking-[0.28em] sm:tracking-[0.3em] text-ember">
            § Chapter IV · Engagements
          </div>
          <div className="md:col-span-6">
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight">
              {t.pricing.title}
            </h2>
          </div>
          <div className="md:col-span-3 text-ink-dim italic font-serif text-sm md:text-base">
            {t.pricing.subtitle}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {t.pricing.plans.map((plan, i) => (
            <motion.div
              key={i}
              data-testid={`pricing-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
              className={`relative p-6 sm:p-8 md:p-10 border ${
                plan.featured
                  ? 'border-ember bg-[rgba(255,90,31,0.04)]'
                  : 'border-[var(--line)] bg-paper-2'
              }`}
              style={{
                clipPath: 'polygon(20px 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%, 0 20px)',
              }}
            >
              {plan.featured && (
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 font-mono text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.25em] text-ember flex items-center gap-2">
                  <span className="dot" /> Signature
                </div>
              )}
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-ink-dim mb-5 sm:mb-6">
                Plan / 0{i + 1}
              </div>
              <h3 className="font-serif text-3xl md:text-4xl mb-4">{plan.name}</h3>
              <div className="flex items-baseline gap-2 mb-5 sm:mb-6 flex-wrap">
                <span className="font-serif text-4xl md:text-5xl">{plan.price}</span>
                <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] text-ink-dim">
                  {plan.period}
                </span>
              </div>
              <p className="text-ink-dim text-sm leading-relaxed mb-6 sm:mb-8 min-h-[64px]">
                {plan.description}
              </p>
              <ul className="space-y-3 mb-8 sm:mb-10">
                {plan.features?.map((f, fi) => (
                  <li key={fi} className="flex items-start gap-3 text-sm">
                    <Check
                      className={`w-4 h-4 mt-[3px] flex-shrink-0 ${
                        plan.featured ? 'text-ember' : 'text-ink-dim'
                      }`}
                      strokeWidth={2}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid={`pricing-cta-${i}`}
                className={plan.featured ? 'btn-ember' : 'btn-ghost'}
              >
                {plan.cta}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
